/**
 * Hooks for the customer provisioning + portal access screens.
 *
 * Schema dependencies:
 *   • customer_users (junction)            — migration 0011
 *   • contacts (Zoho-mastered identity)    — migration 0010 (main)
 *   • zoho_sync_state, zoho_webhook_events — migration 0010 (main)
 *   • customers.zoho_account_id, .is_zoho_synced, .portal_active — 0010
 *
 * Invitations are queued with verified actor/session context. Revocation
 * commits synchronously through an authenticated, version-checked RPC.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { invokeTriggerTask, TriggerTaskError } from '../lib/triggerTask';
import { useAuth } from '../contexts/AuthContext';
import { canManageCustomerAccess, revokeCustomerAccess, type RevokeCustomerAccessInput } from '../lib/customerAccess';

export type CustomerUserStatus = 'invited' | 'active' | 'disabled' | 'revoked';
export type DisableReason =
  | 'zoho_status_change'
  | 'admin_revoked'
  | 'never_logged_in'
  | 'security_policy';

export interface CustomerUserRow {
  id: string;
  user_id: string;
  customer_id: string;
  contact_id: string | null;
  zoho_contact_id: string | null;
  role: string;
  status: CustomerUserStatus;
  access_version: number;
  is_primary_contact: boolean;
  invited_at: string | null;
  accepted_at: string | null;
  last_login_at: string | null;
  disabled_at: string | null;
  disabled_reason: DisableReason | null;
  created_at: string;
  user: {
    id: string;
    email: string;
    full_name: string | null;
  };
}

const CUSTOMER_USER_COLUMNS = `id, user_id, customer_id, contact_id, zoho_contact_id, role, status, access_version,
  is_primary_contact, invited_at, accepted_at, last_login_at,
  disabled_at, disabled_reason, created_at,
  user:users!customer_users_user_id_fkey(id, email, full_name)`;

export function useCustomerUsers(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-users', customerId],
    enabled: !!customerId,
    queryFn: async (): Promise<CustomerUserRow[]> => {
      const { data, error } = await supabase
        .from('customer_users')
        .select(CUSTOMER_USER_COLUMNS)
        .eq('customer_id', customerId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as CustomerUserRow[];
    },
  });
}

export function useAllCustomerUsers() {
  return useQuery({
    queryKey: ['customer-users-all'],
    queryFn: async (): Promise<CustomerUserRow[]> => {
      const { data, error } = await supabase
        .from('customer_users')
        .select(CUSTOMER_USER_COLUMNS)
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as CustomerUserRow[];
    },
  });
}

// ─── Zoho sync dashboard reads main's zoho_sync_state + zoho_webhook_events ──

export type ZohoModule = 'Accounts' | 'Contacts' | 'Deals';

export interface ZohoSyncStateRow {
  module: ZohoModule;
  last_pull_at: string | null;
  last_pull_token: string | null;
  last_push_at: string | null;
  last_run_status: 'ok' | 'partial' | 'error' | null;
  last_run_error: string | null;
  records_pulled: number;
  records_pushed: number;
  updated_at: string;
}

export function useZohoSyncState() {
  return useQuery({
    queryKey: ['zoho-sync-state'],
    queryFn: async (): Promise<ZohoSyncStateRow[]> => {
      const { data, error } = await supabase
        .from('zoho_sync_state')
        .select('module, last_pull_at, last_pull_token, last_push_at, last_run_status, last_run_error, records_pulled, records_pushed, updated_at')
        .order('module');
      if (error) throw error;
      return (data ?? []) as ZohoSyncStateRow[];
    },
    refetchInterval: 30_000,
  });
}

export interface ZohoWebhookEventRow {
  id: string;
  module: string;
  zoho_record_id: string;
  operation: 'insert' | 'update' | 'delete';
  processed_at: string | null;
  process_error: string | null;
  received_at: string;
}

export function useZohoWebhookEvents(opts: { onlyUnprocessed?: boolean; onlyErrors?: boolean } = {}) {
  return useQuery({
    queryKey: ['zoho-webhook-events', opts],
    queryFn: async (): Promise<ZohoWebhookEventRow[]> => {
      let q = supabase
        .from('zoho_webhook_events')
        .select('id, module, zoho_record_id, operation, processed_at, process_error, received_at')
        .order('received_at', { ascending: false })
        .limit(200);
      if (opts.onlyUnprocessed) q = q.is('processed_at', null);
      if (opts.onlyErrors) q = q.not('process_error', 'is', null);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ZohoWebhookEventRow[];
    },
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

/**
 * Portal invites always create a `viewer`. `users.role` is the RLS staff
 * bypass (`is_paznetworks_staff()`), so a customer contact must never be
 * invited with a staff-level role from this UI.
 */
export interface InviteParams {
  customer_id: string;
  email: string;
  full_name?: string;
  role?: 'viewer';
  is_primary_contact?: boolean;
  contact_id?: string;
  zoho_contact_id?: string;
  reason: string;
  expected_version?: number;
}

/**
 * Queue a portal invite. The auth.admin.inviteUserByEmail call lives in the
 * Trigger.dev `customer-provisioning-invite` task; this hook only queues it.
 */
export function useInviteCustomerUser() {
  const qc = useQueryClient();
  const { role } = useAuth();
  return useMutation({
    mutationFn: async (params: InviteParams) => {
      if (!canManageCustomerAccess(role)) throw new Error('Customer invitations require an admin or superadmin');
      const email = params.email.trim().toLowerCase();
      return invokeTriggerTask(
        'customer-provisioning-invite',
        { ...params, email, role: 'viewer' },
        { idempotencyKey: `customer-invite-${crypto.randomUUID()}` },
      );
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['customer-users', vars.customer_id] });
      qc.invalidateQueries({ queryKey: ['customer-users-all'] });
    },
  });
}

export function useRevokeCustomerUser() {
  const qc = useQueryClient();
  const { role } = useAuth();
  return useMutation({
    mutationFn: async (params: RevokeCustomerAccessInput) => {
      if (!canManageCustomerAccess(role)) throw new Error('Revoking customer access requires an admin or superadmin');
      return revokeCustomerAccess(params, (args) => supabase.rpc('revoke_customer_access', args));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-users'] });
      qc.invalidateQueries({ queryKey: ['customer-users-all'] });
    },
  });
}

/** Modules that have a pull task. Deals arrive by webhook only. */
export function zohoPullTaskFor(module: ZohoModule): 'zoho-pull-accounts' | 'zoho-pull-contacts' | null {
  if (module === 'Accounts') return 'zoho-pull-accounts';
  if (module === 'Contacts') return 'zoho-pull-contacts';
  return null;
}

/** Queues the matching Zoho pull task (zoho-pull-accounts / zoho-pull-contacts). */
export function useTriggerZohoSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (module: ZohoModule) => {
      const task = zohoPullTaskFor(module);
      if (!task) {
        throw new TriggerTaskError(
          'zoho-pull-accounts',
          'not_allowed',
          null,
          `There is no pull task for the ${module} module; it syncs by webhook only.`,
        );
      }
      return invokeTriggerTask(task, { module });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['zoho-sync-state'] });
      qc.invalidateQueries({ queryKey: ['zoho-webhook-events'] });
    },
  });
}

// ─── Customer + contact lookup ───────────────────────────────────────────────

export interface CustomerLite {
  id: string;
  name: string;
  account_number: string;
  zoho_account_id: string | null;
  is_zoho_synced: boolean;
  portal_active: boolean;
  status: string;
}

export function useCustomersLite() {
  return useQuery({
    queryKey: ['customers-lite'],
    queryFn: async (): Promise<CustomerLite[]> => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, account_number, zoho_account_id, is_zoho_synced, portal_active, status')
        .is('deleted_at', null)
        .order('name');
      if (error) throw error;
      return (data ?? []) as CustomerLite[];
    },
  });
}

export interface ContactLite {
  id: string;
  customer_id: string | null;
  zoho_contact_id: string | null;
  email: string | null;
  full_name: string | null;
  title: string | null;
  phone: string | null;
  is_primary: boolean;
  is_zoho_synced: boolean;
}

/** Zoho-synced contacts under a customer — used as the "eligible contacts" list. */
export function useContactsForCustomer(customerId: string | null) {
  return useQuery({
    queryKey: ['contacts', customerId],
    enabled: !!customerId,
    queryFn: async (): Promise<ContactLite[]> => {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, customer_id, zoho_contact_id, email, full_name, title, phone, is_primary, is_zoho_synced')
        .eq('customer_id', customerId!)
        .is('deleted_at', null)
        .order('is_primary', { ascending: false })
        .order('full_name');
      if (error) throw error;
      return (data ?? []) as ContactLite[];
    },
  });
}
